<template>
  <v-dialog
    :value="value"
    max-width="520"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5">綁定驗證碼查詢</span>
        <v-spacer/>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="form">
          <!-- 245 個地區，必須可搜尋；值仍限定從清單選取，不接受自由輸入 -->
          <v-autocomplete
            v-model="region"
            :items="COUNTRY_CODES"
            :filter="filterCountry"
            item-text="label"
            item-value="region"
            label="國家／地區"
            no-data-text="查無此國家或國碼"
            outlined
            dense
          />
          <v-text-field
            v-model="localPhone"
            :hint="phoneHint"
            :rules="phoneRules"
            label="手機"
            persistent-hint
            outlined
            dense
          />
        </v-form>

        <v-card
          v-if="result"
          class="mt-5 pa-5 text-center"
          outlined
        >
          <div class="text-caption grey--text text--darken-1">綁定驗證碼</div>
          <div class="otp-value">{{ result.otp }}</div>
          <div
            v-if="result.otp_prefix"
            class="text-caption grey--text text--darken-1 mt-2"
          >
            簡訊前綴 {{ result.otp_prefix }}
          </div>
          <!-- send_status 有值即代表有發送警告要顯示，內容以後端回傳為準 -->
          <div
            v-if="result.send_status"
            class="orange--text text--darken-2 mt-2"
          >
            <v-icon small color="orange darken-2">mdi-alert</v-icon>
            {{ result.send_status }}
          </div>
          <div class="text-body-2 mt-2">
            產生時間 {{ result.occurred_at }}
          </div>
        </v-card>

        <div
          v-else-if="hasQueried"
          class="text-center grey--text text--darken-1 mt-5"
        >
          查無綁定驗證碼紀錄
        </div>

        <v-alert
          v-if="errorMessage"
          class="mt-5 mb-0"
          type="error"
          dense
          outlined
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn text @click="$emit('input', false)">
          取消
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading"
          @click="queryOtp"
        >
          查詢
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { getLineOtpAPI } from '@/api/member'
import {
  buildOtpQueryPhone,
  COUNTRY_CODES,
  isValidLocalPhone
} from '@/utils/phone'

export default {
  name: 'OtpQueryDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      COUNTRY_CODES,
      region: 'TW',
      localPhone: '',
      result: null,
      hasQueried: false,
      errorMessage: '',
      loading: false,
      requestId: 0
    }
  },

  computed: {
    ...mapGetters(['axiosParamData']),

    phoneHint() {
      return this.region === 'TW'
        ? '例：0912345678'
        : '請輸入當地手機號碼，不含國碼'
    },

    phoneRules() {
      return [
        value => !!value || '請輸入手機號碼',
        value => isValidLocalPhone(this.region, value) || '手機格式不正確'
      ]
    }
  },

  watch: {
    value(isOpen) {
      if (!isOpen) {
        this.resetDialog()
      }
    },

    region() {
      this.clearQueryState()
    },

    localPhone() {
      this.clearQueryState()
    }
  },

  methods: {
    // 同時比對繁中國名、數字國碼與 ISO 代碼，例如「台灣」「886」「TW」都查得到
    filterCountry(item, queryText) {
      const q = queryText.trim().toLowerCase()
      if (!q) return true
      return item.label.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q) ||
        item.region.toLowerCase().includes(q)
    },

    clearQueryState() {
      this.requestId += 1
      this.result = null
      this.hasQueried = false
      this.errorMessage = ''
      this.loading = false
    },

    resetDialog() {
      this.requestId += 1
      this.region = 'TW'
      this.localPhone = ''
      this.result = null
      this.hasQueried = false
      this.errorMessage = ''
      this.loading = false

      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },

    queryOtp() {
      const phone = buildOtpQueryPhone(this.region, this.localPhone)

      if (!phone) {
        this.$refs.form.validate()
        return
      }

      const requestId = this.requestId + 1
      this.requestId = requestId
      this.result = null
      this.hasQueried = false
      this.errorMessage = ''
      this.loading = true

      getLineOtpAPI({
        ...this.axiosParamData,
        phone
      }).then(res => {
        if (requestId !== this.requestId) return

        this.result = res.data.data
        this.hasQueried = true
        this.loading = false
      }).catch(e => {
        if (requestId !== this.requestId) return

        const status = e.response && e.response.status
        const reason = e.response && e.response.data && e.response.data.reason

        if (status === 403 && reason === 'CHANNEL_NOT_FOUND') {
          this.errorMessage = '目前帳號未設定 LINE channel，無法查詢綁定驗證碼'
        } else if (status === 422) {
          this.errorMessage = '手機格式不正確'
        } else if (status === 429) {
          this.errorMessage = '查詢過於頻繁，請稍後再試'
        } else {
          this.errorMessage = '查詢失敗，請稍後再試'
        }

        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.otp-value {
  font-size: 2.5rem;
  letter-spacing: 0.3em;
  line-height: 1.2;
}
</style>
